export type AutoStepKey =
  | 'detect'
  | 'preprocess'
  | 'similar'
  | 'text'
  | 'fingerprint'
  | 'fingerprintRecognition'
  | 'identify';

export type AutoDecisionAction =
  | 'run'
  | 'skipped'
  | 'stop'
  | 'failed'
  | 'manual_override';

export interface AutoDecision {
  id: string;
  step: AutoStepKey;
  action: AutoDecisionAction;
  reason: string;
  startedAt?: number;
  completedAt?: number;
  durationMs?: number;
  confidence?: number;
  detail?: string;
}

export interface AutoMatchConfig {
  acceptThreshold: number;
  candidateThreshold: number;
  minMargin: number;
  allowIdentifyShortcut: boolean;
  detectUploads: boolean;
  preprocessUploads: boolean;
  preprocessDetections: boolean;
}

export const AUTO_MATCH_CONFIG: AutoMatchConfig = {
  acceptThreshold: 0.92,
  candidateThreshold: 0.75,
  minMargin: 0.06,
  allowIdentifyShortcut: true,
  detectUploads: true,
  preprocessUploads: true,
  preprocessDetections: true,
};

export const AUTO_STEP_ORDER: AutoStepKey[] = [
  'detect',
  'preprocess',
  'similar',
  'text',
  'fingerprint',
  'fingerprintRecognition',
  'identify',
];

const cigarId = (candidate: any) =>
  candidate?.cigarId ?? candidate?.cigar_id ?? candidate?.id;

export const automaticCandidateScore = (candidate: any) => {
  const value =
    candidate?.confidence ?? candidate?.score ?? candidate?.similarity;
  const score = Number(value);
  if (!Number.isFinite(score)) return null;
  return score > 1 ? score / 100 : score;
};

const normalizedIdentityPart = (value: unknown) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const researchCandidateAlignment = (candidate: any, result: any) => {
  const hypothesisIndex = Number(candidate?.hypothesisIndex ?? 0);
  const hypothesis = result?.research?.hypotheses?.[hypothesisIndex];
  if (!hypothesis) return 0;

  const brandMatches =
    normalizedIdentityPart(candidate?.brand) ===
    normalizedIdentityPart(hypothesis.brand);
  const nameMatches =
    normalizedIdentityPart(candidate?.name) ===
    normalizedIdentityPart(hypothesis.name);
  return Number(brandMatches) + Number(nameMatches) * 2;
};

const rankedResearchCandidates = (result: any, candidates?: any[]) =>
  (candidates || result?.research?.catalogCandidates || [])
    .map((candidate: any, index: number) => ({
      alignment: researchCandidateAlignment(candidate, result),
      candidate,
      index,
      score: automaticCandidateScore(candidate) ?? -1,
    }))
    .sort(
      (left, right) =>
        right.score - left.score || right.alignment - left.alignment
    );

export const candidatesForAutomaticStep = (
  step: AutoStepKey,
  result: any
): any[] => {
  if (!result) return [];
  if (step === 'text') {
    return [
      ...(result.proposedMatch ? [result.proposedMatch] : []),
      ...(result.textCandidates || result.text_candidates || []),
    ];
  }
  if (step === 'identify') {
    if (result.status === 'matched' && result.match) return [result.match];

    const researchCandidates = rankedResearchCandidates(result).map(
      ({ candidate }) => candidate
    );
    if (result.status !== 'matched') return researchCandidates;

    return [
      ...(result.match ? [result.match] : []),
      ...(result.suggestions || []),
      ...(result.consensus?.candidates || []),
      ...researchCandidates,
    ];
  }
  return result.candidates || [];
};

export const defaultIdentifyCandidateSelection = (result: any) => {
  const researchCandidates = result?.hydratedResearchCandidates?.length
    ? result.hydratedResearchCandidates
    : result?.research?.catalogCandidates || [];
  if (result?.status !== 'matched' && researchCandidates.length > 0) {
    const [preferred] = rankedResearchCandidates(result, researchCandidates);
    return { source: 'research' as const, index: preferred.index };
  }
  if (
    result?.status !== 'matched' &&
    result?.research?.hypotheses?.length > 0
  ) {
    return { source: 'hypothesis' as const, index: 0 };
  }

  const resultCandidates = result?.hydratedCandidates?.length
    ? result.hydratedCandidates
    : candidatesForAutomaticStep('identify', result);
  return resultCandidates.length > 0
    ? { source: 'result' as const, index: 0 }
    : null;
};

export const automaticCandidateStrength = (
  candidates: any[],
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) => {
  const ranked = candidates
    .map((candidate) => ({
      candidate,
      score: automaticCandidateScore(candidate),
    }))
    .filter(
      (item): item is { candidate: any; score: number } => item.score !== null
    )
    .sort((left, right) => right.score - left.score);
  const top = ranked[0];
  const runnerUp = ranked[1];
  const margin = top ? top.score - (runnerUp?.score ?? 0) : 0;
  return {
    candidate: top?.candidate,
    confidence: top?.score ?? null,
    margin,
    sufficient: Boolean(
      top &&
        top.score >= config.acceptThreshold &&
        (!runnerUp || margin >= config.minMargin)
    ),
  };
};

export const shouldAcceptCandidate = (
  step: AutoStepKey,
  result: any,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) => {
  const strength = automaticCandidateStrength(
    candidatesForAutomaticStep(step, result),
    config
  );
  if (step === 'identify' && result?.status !== 'matched') {
    return { ...strength, sufficient: false };
  }
  return strength;
};

export const shouldRunDetect = (
  sourceType: string,
  workingImageType: string,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) =>
  config.detectUploads &&
  sourceType === 'upload' &&
  workingImageType === 'original';

export const shouldRunPreprocess = (
  sourceType: string,
  workingImageType: string,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) =>
  (workingImageType === 'detected' && config.preprocessDetections) ||
  (sourceType === 'upload' &&
    workingImageType === 'original' &&
    config.preprocessUploads);

export const shouldRunSimilar = (preprocessStatus?: string) =>
  preprocessStatus !== 'skipped';

export const shouldRunText = (context: {
  detectResult?: any;
  preprocessResult?: any;
}) => {
  const cheapTextSignal =
    context.preprocessResult?.textLikely ??
    context.preprocessResult?.textVisible ??
    context.detectResult?.textLikely ??
    context.detectResult?.textVisible;
  return cheapTextSignal === false
    ? { run: false, reason: 'no_text_likely_visible' }
    : { run: true, reason: 'text_presence_unknown_conservative_fallback' };
};

const usefulTextEvidence = (result: any) =>
  Boolean(
    result?.rawText?.trim?.() ||
      result?.normalizedText?.trim?.() ||
      result?.tokens?.length ||
      candidatesForAutomaticStep('text', result).length
  );

const topCandidateId = (step: AutoStepKey, result: any) => {
  const { candidate } = automaticCandidateStrength(
    candidatesForAutomaticStep(step, result)
  );
  return candidate ? cigarId(candidate) : null;
};

export const hasMeaningfulCandidateConflict = (
  stages: Partial<Record<AutoStepKey, { result?: any }>>,
  currentStep: AutoStepKey,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) => {
  const currentCandidates = candidatesForAutomaticStep(
    currentStep,
    stages[currentStep]?.result
  );
  const current = automaticCandidateStrength(currentCandidates, config);
  const currentId = current.candidate ? cigarId(current.candidate) : null;
  if (!currentId || (current.confidence || 0) < config.candidateThreshold) {
    return false;
  }
  return (['similar', 'text', 'fingerprintRecognition'] as AutoStepKey[])
    .filter((step) => step !== currentStep && stages[step]?.result)
    .some((step) => {
      const prior = automaticCandidateStrength(
        candidatesForAutomaticStep(step, stages[step]?.result),
        config
      );
      const priorId = prior.candidate ? cigarId(prior.candidate) : null;
      return Boolean(
        priorId &&
          (prior.confidence || 0) >= config.candidateThreshold &&
          String(priorId) !== String(currentId)
      );
    });
};

export const shouldJumpToIdentify = (
  stages: Partial<Record<AutoStepKey, { result?: any }>>,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) => {
  if (!config.allowIdentifyShortcut || !stages.text?.result) return false;
  const similarStrength = automaticCandidateStrength(
    candidatesForAutomaticStep('similar', stages.similar?.result),
    config
  );
  if (
    similarStrength.confidence !== null &&
    similarStrength.confidence >= config.candidateThreshold &&
    usefulTextEvidence(stages.text.result)
  ) {
    return true;
  }
  const similarId = topCandidateId('similar', stages.similar?.result);
  const textId = topCandidateId('text', stages.text.result);
  return Boolean(similarId && textId && String(similarId) === String(textId));
};

export const automaticResultSummary = (
  step: AutoStepKey,
  result: any,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) => {
  const strength = shouldAcceptCandidate(step, result, config);
  const hypothesis = result?.research?.hypotheses?.[0];
  const candidate = strength.candidate || hypothesis;
  const confidence =
    strength.confidence ?? automaticCandidateScore(hypothesis) ?? null;
  return {
    candidate,
    confidence,
    confident: strength.sufficient,
    label: candidate
      ? [candidate.brand, candidate.name || candidate.label]
          .filter(Boolean)
          .join(' ') || 'Candidate returned'
      : null,
  };
};
